import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Eye, EyeOff, Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

/**
 * ResetPasswordPage
 *
 * Landing page for the "reset password" email link.
 *
 * Supabase's `detectSessionInUrl: true` option (in supabaseClient.js) auto-
 * materializes a temporary session from the URL fragment on load, and fires
 * `PASSWORD_RECOVERY` via onAuthStateChange. AuthContext exposes this as
 * `isPasswordRecovery`.
 *
 * We wait briefly for that event before deciding "link expired" — Supabase's
 * URL parsing is synchronous but the event dispatch is async.
 */
const MIN_PASSWORD_LEN = 8;

const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isPasswordRecovery, updatePassword, loading } = useAuth();

  const [pw, setPw] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Give Supabase a brief window to fire PASSWORD_RECOVERY after mount.
  const [gracePeriodOver, setGracePeriodOver] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setGracePeriodOver(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const hasRecoverySession = isPasswordRecovery || !!user;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (pw.length < MIN_PASSWORD_LEN) {
      setError(t('auth.reset_pw_too_short', { min: MIN_PASSWORD_LEN }));
      return;
    }
    if (pw !== pwConfirm) {
      setError(t('auth.reset_pw_mismatch'));
      return;
    }

    setSubmitting(true);
    const { error: upErr } = await updatePassword(pw);
    setSubmitting(false);

    if (upErr) {
      setError(upErr.message || t('auth.reset_generic_error'));
      return;
    }
    setSuccess(true);
    // After 2s, redirect to dashboard — the user is now signed in via the
    // materialized recovery session, which becomes a full session after
    // updateUser succeeds.
    setTimeout(() => navigate('/dashboard', { replace: true }), 1800);
  };

  // Loading state while AuthContext hydrates + Supabase parses URL fragment.
  if (loading || (!hasRecoverySession && !gracePeriodOver)) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-background"
        data-testid="reset-password-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-[hsl(var(--primary))]" />
      </div>
    );
  }

  return (
    <div
      className="relative min-h-screen w-full overflow-hidden bg-background text-foreground"
      data-testid="reset-password-page"
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(120% 80% at 50% 0%, hsl(var(--primary) / 0.10) 0%, hsl(var(--background)) 60%)',
        }}
      />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="mb-6 flex flex-col items-center text-center space-y-3">
            <img
              src="/kocore-logo.png"
              alt="K.O. CO+RE Systems"
              className="h-28 w-auto"
              data-testid="reset-brand-logo"
            />
            <h1 className="text-xl font-semibold tracking-tight">
              {success ? t('auth.reset_success_title') : t('auth.reset_title')}
            </h1>
            <p className="text-sm text-muted-foreground max-w-xs">
              {success
                ? t('auth.reset_success_subtitle')
                : hasRecoverySession
                ? t('auth.reset_subtitle')
                : t('auth.reset_link_expired_subtitle')}
            </p>
          </div>

          <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] shadow-2xl shadow-black/40">
            <CardContent className="p-6 sm:p-8">
              {success ? (
                <div
                  className="flex flex-col items-center text-center gap-4"
                  data-testid="reset-success"
                >
                  <div className="h-12 w-12 rounded-full bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center">
                    <CheckCircle2 className="h-6 w-6 text-[hsl(var(--primary))]" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('auth.reset_success_body')}
                  </p>
                </div>
              ) : !hasRecoverySession ? (
                <div
                  className="flex flex-col items-center text-center gap-4"
                  data-testid="reset-link-expired"
                >
                  <div className="h-12 w-12 rounded-full bg-[hsl(var(--destructive))]/10 border border-[hsl(var(--destructive))]/30 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-[hsl(var(--destructive))]" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('auth.reset_link_expired_body')}
                  </p>
                  <Link
                    to="/forgot-password"
                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-[hsl(var(--primary))] hover:underline underline-offset-4"
                    data-testid="reset-request-new-link"
                  >
                    {t('auth.reset_request_new')}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                  <div className="space-y-2">
                    <Label
                      htmlFor="reset-new-pw"
                      className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
                    >
                      {t('auth.new_password')}
                    </Label>
                    <div className="relative">
                      <Input
                        id="reset-new-pw"
                        type={showPw ? 'text' : 'password'}
                        autoComplete="new-password"
                        placeholder="••••••••"
                        value={pw}
                        onChange={(e) => setPw(e.target.value)}
                        aria-invalid={!!error}
                        className="h-11 pr-11 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 hover:border-[hsl(var(--primary))]/30 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 focus-visible:border-[hsl(var(--primary))]/40 transition-colors"
                        data-testid="reset-new-pw-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        aria-label={showPw ? t('auth.hide_password') : t('auth.show_password')}
                        aria-pressed={showPw}
                        className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--primary))]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 transition-colors"
                        data-testid="reset-new-pw-toggle"
                      >
                        {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    <p className="text-[11px] text-muted-foreground/80">
                      {t('auth.pw_hint', { min: MIN_PASSWORD_LEN })}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="reset-confirm-pw"
                      className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground"
                    >
                      {t('auth.confirm_password')}
                    </Label>
                    <Input
                      id="reset-confirm-pw"
                      type={showPw ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={pwConfirm}
                      onChange={(e) => setPwConfirm(e.target.value)}
                      aria-invalid={!!error}
                      className="h-11 bg-[hsl(var(--muted))] border-border text-foreground placeholder:text-muted-foreground/60 hover:border-[hsl(var(--primary))]/30 focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 focus-visible:border-[hsl(var(--primary))]/40 transition-colors"
                      data-testid="reset-confirm-pw-input"
                    />
                  </div>

                  {error ? (
                    <Alert
                      variant="default"
                      className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
                      data-testid="reset-error-message"
                    >
                      <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
                      <AlertDescription className="text-sm">{error}</AlertDescription>
                    </Alert>
                  ) : null}

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="group relative w-full h-11 bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/85 active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]/40 transition-colors font-semibold tracking-[0.16em] uppercase"
                    data-testid="reset-submit-button"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('auth.reset_saving')}
                      </>
                    ) : (
                      <>
                        {t('auth.reset_set_password')}
                        <ArrowRight className="h-4 w-4 ml-2 transition-transform group-hover:translate-x-0.5" />
                      </>
                    )}
                  </Button>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
                      data-testid="reset-back-to-login-link"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      {t('auth.back_to_login')}
                    </Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>

          <p className="mt-6 text-center text-[11px] uppercase tracking-[0.28em] text-muted-foreground/70">
            © K.O. CO+RE SYSTEMS · Secure Terminal
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
